import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  PeopleAlt as PeopleAltIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  Fingerprint as FingerprintIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  PointOfSale as PointOfSaleIcon,
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  Extension as ExtensionIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

interface CompanyCardProps {
  id: string;
  name: string;
  logo?: string;
  admin: {
    name: string;
    email: string;
    phone?: string;
  };
  maxPersonnel?: number;
  createdAt?: string;
  contract?: {
    startDate: number;
    endDate: number;
    daysRemaining: number;
    isExpired: boolean;
    /** Sözleşmeyi giren bayi (null = merkez girdi). */
    dealerId?: number | null;
    dealerName?: string | null;
  } | null;
  loginActivity?: {
    lastLogin: number;
    loginCount: number;
    lastPlatform: string;
  } | null;
  /** AI chat asistanı bu şirket için açık mı (kart üzerinde ikon rozeti) */
  aiChatEnabled?: boolean;
  /** Şirket bir bayiye atanmışsa bayi adı (kart üzerinde chip). */
  dealerName?: string | null;
  /**
   * Sözleşmeyi giren bayi rozeti gösterilsin mi. Yalnız merkez görünümünde anlamlı;
   * bayi kendi adını görmesin (dealerName chip'iyle aynı mantık).
   */
  showContractDealer?: boolean;
  /** Demo bitiş tarihi (ISO). null/undefined = demo değil. */
  demoUntil?: string | null;
  /** Demo süresi doldu mu (sunucuda hesaplanır). */
  demoExpired?: boolean;
  subscriptionUntil?: string | null;
  subscriptionSource?: 'google_play' | 'app_store' | null;
  subscriptionAutoRenewing?: boolean | null;
  demoCarryOverDays?: number | null;
  accessState?: 'none' | 'active' | 'grace' | 'expired' | null;
  graceUntil?: string | null;
  /**
   * Bayinin açtığı şirkete merkez henüz hiç modül izni vermedi.
   * Hem merkez hem bayi görünümünde uyarı rozeti çıkar.
   */
  awaitingModules?: boolean;
  onViewDetails: () => void;
  /** Verilmezse (ör. bayi görünümü) menüde "Şirketi Sil" çıkmaz. */
  onDelete?: (companyId: string, companyName: string) => void;
  onCreateContract?: (e?: React.MouseEvent) => void;
  onFiscalDevices?: (e?: React.MouseEvent) => void;
  onSyncToSql?: (companyId: string, companyName: string) => Promise<void> | void;
  onAiChat?: (e?: React.MouseEvent) => void;
  /** Şirket bazlı modül aç/kapa diyaloğunu açar */
  onModules?: (e?: React.MouseEvent) => void;
  /** "Bayiye Ata" kısayolu (yalnız süper admin görünümünde verilir). */
  onAssignDealer?: (e?: React.MouseEvent) => void;
}

const CompanyCard: React.FC<CompanyCardProps> = ({
  id,
  name,
  logo,
  admin,
  maxPersonnel,
  createdAt,
  contract,
  loginActivity,
  aiChatEnabled,
  dealerName,
  showContractDealer,
  demoUntil,
  demoExpired,
  subscriptionUntil,
  subscriptionSource,
  subscriptionAutoRenewing,
  demoCarryOverDays,
  accessState,
  awaitingModules,
  onViewDetails,
  onDelete,
  onCreateContract,
  onFiscalDevices,
  onSyncToSql,
  onAiChat,
  onModules,
  onAssignDealer
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    onViewDetails();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    if (onDelete) {
      onDelete(id, name);
    }
  };

  const handleContractClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    if (onCreateContract) {
      onCreateContract(e);
    }
  };

  const handleFiscalDevicesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    if (onFiscalDevices) {
      onFiscalDevices(e);
    }
  };

  const handleAiChatClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    if (onAiChat) {
      onAiChat(e);
    }
  };

  const handleModulesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    if (onModules) {
      onModules(e);
    }
  };

  const handleAssignDealerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    if (onAssignDealer) {
      onAssignDealer(e);
    }
  };

  const handleSyncToSqlClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    if (!onSyncToSql || isSyncing) return;
    try {
      setIsSyncing(true);
      await onSyncToSql(id, name);
    } finally {
      setIsSyncing(false);
    }
  };

  // Demo rozeti: kalan gün (görsel amaçlı; asıl kilit kararı sunucuda)
  const demoDaysLeft = demoUntil
    ? Math.max(0, Math.ceil((new Date(demoUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const hasMobileSubscription = !!subscriptionUntil || !!subscriptionSource;
  const subscriptionStoreLabel = subscriptionSource === 'google_play'
    ? 'Google Play'
    : subscriptionSource === 'app_store'
      ? 'App Store'
      : null;
  const subscriptionRenewLabel = subscriptionAutoRenewing === true
    ? 'Otomatik yenileme açık'
    : subscriptionAutoRenewing === false
      ? 'Otomatik yenileme kapalı'
      : null;
  const subscriptionCarryLabel = demoCarryOverDays == null
    ? null
    : demoCarryOverDays > 0
      ? `Aktarılan demo: ${demoCarryOverDays} gün`
      : 'Demo kullanılmış';
  const subscriptionTooltip = [subscriptionStoreLabel, subscriptionRenewLabel, subscriptionCarryLabel]
    .filter(Boolean)
    .join(' · ');
  const subscriptionEndLabel = (() => {
    if (!subscriptionUntil) return '-';
    try {
      return new Date(subscriptionUntil).toLocaleDateString('tr-TR');
    } catch {
      return '-';
    }
  })();
  const subscriptionLabel = accessState === 'grace'
    ? 'Süre doldu (son gün)'
    : accessState === 'expired'
      ? 'Abonelik bitti'
      : `Mobil abonelik: ${subscriptionEndLabel}`;
  const subscriptionColor: 'warning' | 'error' | 'info' = accessState === 'expired'
    ? 'error'
    : accessState === 'grace'
      ? 'warning'
      : 'info';

  // Tarih formatını DD.MM.YYYY şeklinde düzenle
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR');
    } catch (error) {
      return '-';
    }
  };

  return (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      border: contract ? '2px solid #66bb6a' : 'none',
      bgcolor: contract ? 'rgba(102, 187, 106, 0.08)' : 'white',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 3
      }
    }}>
      <Box sx={{ 
        p: 1.5, 
        display: 'flex', 
        alignItems: 'center',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        <Avatar
          src={logo}
          alt={name}
          sx={{ 
            width: 45, 
            height: 45,
            mr: 1.5,
            bgcolor: '#1976d2',
            fontSize: '1rem'
          }}
        >
          {name.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {name}
            </Typography>
            {aiChatEnabled && (
              <Tooltip title="AI Chat Asistanı açık">
                <AutoAwesomeIcon sx={{ fontSize: '0.95rem', color: '#9c27b0', flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {admin?.name || 'Admin Bilgisi Yok'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
            {admin?.email || 'Email Bilgisi Yok'}
          </Typography>
        </Box>
        
        {/* 3 Nokta Menu Butonu */}
        <IconButton 
          onClick={handleMenuClick}
          size="small"
          sx={{ 
            position: 'absolute',
            top: 4,
            right: 4,
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.08)'
            }
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
      
      {/* İletişim ve Tarih Bilgileri */}
      <CardContent sx={{ flexGrow: 1, pt: 1.5, pb: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <PhoneIcon sx={{ fontSize: '1rem', color: '#666', mr: 0.75 }} />
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {admin?.phone || 'Telefon Bilgisi Yok'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <EventIcon sx={{ fontSize: '1rem', color: '#666', mr: 0.75 }} />
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {formatDate(createdAt)}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <PeopleAltIcon sx={{ fontSize: '1rem', color: '#666', mr: 0.75 }} />
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            Max: {maxPersonnel || 0}
          </Typography>
        </Box>
        
        {(dealerName || demoUntil || hasMobileSubscription || awaitingModules) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
            {awaitingModules && (
              <Tooltip title="Merkez bu şirket için henüz modül izni vermedi — kullanıcılar hiçbir menü göremez">
                <Chip
                  icon={<ExtensionIcon sx={{ fontSize: '0.85rem' }} />}
                  label="Modül bekliyor"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                />
              </Tooltip>
            )}
            {dealerName && (
              <Tooltip title="Bu şirket bir bayiye atanmış">
                <Chip
                  icon={<StorefrontIcon sx={{ fontSize: '0.85rem' }} />}
                  label={dealerName}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem', maxWidth: '100%' }}
                />
              </Tooltip>
            )}
            {demoUntil && (
              <Tooltip
                title={`Demo bitiş: ${new Date(demoUntil).toLocaleDateString('tr-TR')}`}
              >
                <Chip
                  label={demoExpired ? 'Demo doldu' : `Demo: ${demoDaysLeft} gün`}
                  size="small"
                  color={demoExpired ? 'error' : 'warning'}
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                />
              </Tooltip>
            )}
            {hasMobileSubscription && (
              <Tooltip title={subscriptionTooltip || 'Mobil abonelik'}>
                <Chip
                  label={subscriptionLabel}
                  size="small"
                  color={subscriptionColor}
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                />
              </Tooltip>
            )}
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FingerprintIcon sx={{ fontSize: '1rem', color: '#666', mr: 0.75 }} />
          <Tooltip title="Şirket ID">
            <Typography variant="body2" sx={{ 
              fontFamily: 'monospace', 
              fontSize: '0.65rem',
              bgcolor: 'rgba(0,0,0,0.05)',
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}>
              {id}
            </Typography>
          </Tooltip>
        </Box>
      </CardContent>
      
      {/* Sözleşme Badge */}
      {contract && (
        <Box sx={{
          bgcolor: contract.isExpired ? '#f44336' : '#66bb6a',
          color: 'white',
          px: 2,
          py: 0.5,
          textAlign: 'center',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          mt: 'auto'
        }}>
          {contract.isExpired 
            ? '⚠️ Sözleşme Süresi Doldu' 
            : `📅 ${contract.daysRemaining} gün kaldı - ${new Date(contract.endDate).toLocaleDateString('tr-TR')}`
          }
          {/* Sözleşmeyi bayi girdiyse ayırt edilsin — merkez kimin sattığını bilmeli. */}
          {showContractDealer && contract.dealerName && (
            <Typography
              component="div"
              sx={{ fontSize: '0.65rem', fontWeight: 400, opacity: 0.95 }}
            >
              Sözleşmeyi giren bayi: {contract.dealerName}
            </Typography>
          )}
        </Box>
      )}
      
      {/* Login Activity Badge - Sadece Aktif Şirketler Sekmesinde */}
      {loginActivity && (
        <Box sx={{
          bgcolor: '#1976d2',
          color: 'white',
          px: 2,
          py: 0.75,
          textAlign: 'center',
          fontSize: '0.7rem',
          mt: contract ? 0 : 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
              🔐 Son Giriş:
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {new Date(loginActivity.lastLogin).toLocaleDateString('tr-TR')} {new Date(loginActivity.lastLogin).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
              📊 Giriş Sayısı:
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {loginActivity.loginCount} kez
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
              💻 Platform:
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {loginActivity.lastPlatform === 'web' ? '🌐 Web' : 
               loginActivity.lastPlatform === 'mobile-android' ? '📱 Android' :
               loginActivity.lastPlatform === 'mobile-ios' ? '🍎 iOS' : loginActivity.lastPlatform}
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={(e) => handleViewDetails(e)}>
          <ListItemIcon>
            <PeopleAltIcon fontSize="small" sx={{ color: '#1976d2' }} />
          </ListItemIcon>
          <ListItemText>Personel Sayısını Güncelle</ListItemText>
        </MenuItem>
        {onFiscalDevices && (
          <MenuItem onClick={(e) => handleFiscalDevicesClick(e)}>
            <ListItemIcon>
              <PointOfSaleIcon fontSize="small" sx={{ color: '#ff9800' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#ff9800' }}>Yazarkasa Cihazları</ListItemText>
          </MenuItem>
        )}
        {onCreateContract && (
          <MenuItem onClick={(e) => handleContractClick(e)}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" sx={{ color: '#66bb6a' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#66bb6a' }}>
              {contract ? 'Sözleşmeyi Güncelle' : 'Sözleşme Yapıldı'}
            </ListItemText>
          </MenuItem>
        )}
        {onAiChat && (
          <MenuItem onClick={(e) => handleAiChatClick(e)}>
            <ListItemIcon>
              <AutoAwesomeIcon fontSize="small" sx={{ color: '#9c27b0' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#9c27b0' }}>
              AI Chat {aiChatEnabled ? '(Açık)' : '(Kapalı)'}
            </ListItemText>
          </MenuItem>
        )}
        {onModules && (
          <MenuItem onClick={(e) => handleModulesClick(e)}>
            <ListItemIcon>
              <ExtensionIcon fontSize="small" sx={{ color: '#0288d1' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#0288d1' }}>Modüller</ListItemText>
          </MenuItem>
        )}
        {onAssignDealer && (
          <MenuItem onClick={(e) => handleAssignDealerClick(e)}>
            <ListItemIcon>
              <StorefrontIcon fontSize="small" sx={{ color: '#7b1fa2' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#7b1fa2' }}>
              {dealerName ? `Bayi: ${dealerName}` : 'Bayiye Ata'}
            </ListItemText>
          </MenuItem>
        )}
        {onSyncToSql && (
          <MenuItem onClick={(e) => handleSyncToSqlClick(e)} disabled={isSyncing}>
            <ListItemIcon>
              {isSyncing ? (
                <CircularProgress size={16} sx={{ color: '#1976d2' }} />
              ) : (
                <CloudUploadIcon fontSize="small" sx={{ color: '#1976d2' }} />
              )}
            </ListItemIcon>
            <ListItemText sx={{ color: '#1976d2' }}>
              {isSyncing ? 'Senkronize Ediliyor...' : "SQL'e Senkronize Et"}
            </ListItemText>
          </MenuItem>
        )}
        {onDelete && <Divider />}
        {onDelete && (
          <MenuItem onClick={(e) => handleDelete(e)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#f44336' }}>Şirketi Sil</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export default CompanyCard; 